import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {Info, Lock, Receipt, ShieldCheck, Users} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import {useReservation} from 'context/reservation';
import {usePendingPaymentSteps} from './usePendingPaymentSteps';

const PAYMENT_ICONS: Record<string, ReactNode> = {
  booking_payments: <Lock size={16} />,
  taxes: <Receipt size={16} />,
  property_protection: <ShieldCheck size={16} />,
};

function WelcomeSteps({totalGuests}: {totalGuests: number}) {
  const {t} = useTranslation();
  const payments = usePendingPaymentSteps();

  const steps = [
    {
      icon: <Users size={16} />,
      title: t('welcome_step_register_guests', {count: totalGuests}),
      hint: t('welcome_step_register_guests_hint'),
    },
    ...payments.map(step => ({
      icon: PAYMENT_ICONS[step.kind],
      title: t(step.title),
      hint: t(step.hint),
    })),
  ];

  return (
    <div data-guest-count-next className="mt-auto pt-[18px]">
      <p className="mb-2 text-[12px] font-bold uppercase tracking-[0.08em] text-[var(--chekin-text-secondary)]">
        {t('guest_count_next_title')}
      </p>
      <ul className="flex flex-col gap-3">
        {steps.map(step => (
          <li key={step.title} className="flex items-center gap-3">
            <span
              aria-hidden
              className="grid size-[34px] shrink-0 place-items-center rounded-[11px] bg-[var(--chekin-blue-tint-info)] text-[var(--chekin-color-brand-blue)]"
            >
              {step.icon}
            </span>
            <span className="min-w-0">
              <b className="block text-[14px] font-semibold leading-5 text-[var(--chekin-color-brand-navy)]">
                {step.title}
              </b>
              <span
                data-guest-count-hint
                className="block text-[13px] leading-[18px] text-[var(--chekin-text-secondary)]"
              >
                {step.hint}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function WelcomeGuestCountNote({
  bookedGuests = 0,
  totalGuests,
  isAskingAges,
}: {
  bookedGuests?: number;
  totalGuests: number;
  isAskingAges: boolean;
}) {
  const {t} = useTranslation();
  const {allowEditingGuestNumber} = useReservation();
  const isOverLimit = !allowEditingGuestNumber && totalGuests > bookedGuests;

  const getNote = () => {
    if (isOverLimit) return t('welcome_guest_count_mismatch', {count: bookedGuests});
    if (allowEditingGuestNumber) return t('guest_count_match_note');
    if (isAskingAges) {
      return t('guest_count_ages_note', {
        guests: t('guests_count', {count: bookedGuests}),
      });
    }
    return null;
  };

  const note = getNote();

  return (
    <p
      data-guest-count-note={isOverLimit ? undefined : ''}
      aria-live="polite"
      className={cn(
        'flex gap-2 px-0.5 text-[13px] leading-[19px]',
        note && 'mt-2.5',
        isOverLimit
          ? 'text-[var(--chekin-red-soft)]'
          : 'text-[var(--chekin-text-secondary)]',
      )}
    >
      {isOverLimit && <Info size={16} className="mt-0.5 shrink-0" aria-hidden />}
      <span>{note}</span>
    </p>
  );
}

export {WelcomeGuestCountNote, WelcomeSteps};
